import { useEffect, useState } from 'react';
import { BellOff, BellRing } from 'lucide-react';
import { usePartyState } from '../App';
import { party } from '../net/client';
import { loadServerConfig } from './serverConfig';

/**
 * Turn alerts: a phone notification when an async game is waiting on you (and when it ends). Uses the
 * browser's Web Push with the server's public VAPID key; the subscription is tied to this player.
 */
type Support = 'checking' | 'ok' | 'no-server' | 'unsupported' | 'install-first' | 'blocked';

const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const standalone = () => window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  return (await (await registration())?.pushManager.getSubscription()) ?? null;
}

function asJson(sub: PushSubscription) {
  const j = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  return { endpoint: j.endpoint, keys: { p256dh: j.keys.p256dh, auth: j.keys.auth } };
}

/** Re-sends this device's subscription after connecting, so the server always knows it (call once online). */
export async function syncTurnAlerts() {
  if (!('PushManager' in window) || Notification.permission !== 'granted') return;
  const sub = await currentSubscription();
  if (sub) party.pushSubscribe(asJson(sub));
}

async function detect(): Promise<Support> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return isIos() && !standalone() ? 'install-first' : 'unsupported';
  const cfg = await loadServerConfig();
  if (!cfg?.pushKey) return 'no-server';
  if (!(await registration())) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  return 'ok';
}

export function TurnAlerts({ compact = false }: { compact?: boolean }) {
  const { push, status } = usePartyState();
  const [support, setSupport] = useState<Support>('checking');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void detect().then((s) => live && setSupport(s));
    return () => {
      live = false;
    };
  }, []);
  // Ask the server whether this device is signed up (it may have expired or moved to another player).
  useEffect(() => {
    if (support !== 'ok' || status !== 'online') return;
    void currentSubscription().then((sub) => (sub ? party.pushCheck(sub.endpoint) : undefined));
  }, [support, status]);

  const on = support === 'ok' && !!push?.on && Notification.permission === 'granted';

  const turnOn = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setSupport(permission === 'denied' ? 'blocked' : 'ok');
        return;
      }
      const cfg = await loadServerConfig();
      const reg = await registration();
      if (!cfg?.pushKey || !reg) return setSupport(!cfg?.pushKey ? 'no-server' : 'unsupported');
      const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(cfg.pushKey) }));
      party.pushSubscribe(asJson(sub));
    } catch {
      setProblem("Couldn't turn alerts on here. Try again, or check this browser's notification settings.");
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      const sub = await currentSubscription();
      if (sub) {
        party.pushUnsubscribe(sub.endpoint);
        await sub.unsubscribe();
      }
    } finally {
      setBusy(false);
    }
  };

  if (support === 'checking' || support === 'unsupported' || support === 'no-server') {
    if (compact || support === 'checking') return null;
    return (
      <p className="text-sm text-mute flex items-center gap-2" data-testid="turn-alerts-unavailable">
        <BellOff size={16} aria-hidden /> {support === 'no-server' ? 'Turn alerts are not set up on this server yet.' : "This browser can't show turn alerts."}
      </p>
    );
  }
  return (
    <section className="card p-4 flex flex-col gap-2" data-testid="turn-alerts">
      <div className="flex items-center gap-3">
        {on ? <BellRing size={20} aria-hidden className="text-[var(--accent)] shrink-0" /> : <BellOff size={20} aria-hidden className="text-mute shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="font-display font-black text-lg leading-tight">Turn alerts</p>
          <p className="text-sm text-mute">
            {support === 'install-first'
              ? 'On iPhone and iPad, add this app to your Home Screen first (Share, then Add to Home Screen), then open it from there.'
              : support === 'blocked'
                ? 'Notifications are blocked for this site. Allow them in your browser settings to get turn alerts.'
                : on
                  ? 'On for this device: you get a ping when an async game is waiting on you, and when it ends.'
                  : 'Get a ping on this device when an async game is waiting on you.'}
          </p>
        </div>
        {support === 'ok' && (
          <button className={`btn btn-sm ${on ? 'btn-secondary' : ''}`} disabled={busy} aria-pressed={on} onClick={() => void (on ? turnOff() : turnOn())} data-testid="turn-alerts-toggle">
            {on ? 'Turn off' : 'Turn on'}
          </button>
        )}
      </div>
      {problem && <p className="text-sm text-bad" role="alert">{problem}</p>}
    </section>
  );
}
